import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import type {
    ExcludeStatusFields,
    SalesforceMappingField,
    ExcludeAccountFields,
} from "app/pages/sales-intelligence/common-components/export-table/types";
import { INTEGRATION_STATUS } from "app/pages/sales-intelligence/pages/management/constants/constants";
import type { FetchError } from "app/pages/sales-intelligence/types";

import type { CrmExportLimit, IntegrationStatus } from "../../components/types";
import { CrmFeedLevel, CRMListObjectsType } from "../../types";
import { managementActions } from "../action-creators";

// NOTE: BE:IntegrationLeadFilter => FE: Exclusion
export type SalesforceIntegrationState = {
    crmConnectionStatus: IntegrationStatus;
    crmStateInfo: {
        accountExportFieldsCount: number;
        contactExportFieldsCount: number;
        leadExportFieldsCount: number;
    };
    crmExportLimit: CrmExportLimit | null;
    toggleLoading: {
        isContactsLoading: boolean;
        isLeadsLoading: boolean;
        isAccountsLoading: boolean;
    };
    companyAsLeadExportClaimEnabled: boolean;
    predefinedOnCreateUpdateClaimEnabled: boolean;
    isCurrentUserVerifiedByCrm: boolean;
    excludeStatusFields: ExcludeStatusFields;
    contactFields: string[];
    leadFields: string[];
    isLoadingExcludeStatusFields: boolean;
    isLoadingMappingFields: boolean;
    mappingFields: SalesforceMappingField[];
    mappingFieldsError: FetchError | null;
    isExclusionOn: boolean | null;
    exclusionError: string;
    isExclusionLoading: boolean;
    error: string;
    isLoading: boolean;
    excludeAccountFields: ExcludeAccountFields;
    isLoadingExcludeAccountFields: boolean;
    isUpdatingFilterFields: boolean;
    isUpdatingStatusField: boolean;
};

const SALESFORCE_INTEGRATION_STATE: SalesforceIntegrationState = {
    crmConnectionStatus: {
        ownerFirstName: "",
        ownerLastName: "",
        domainsCount: 0,
        syncStatus: INTEGRATION_STATUS.NotIntegrated,
        syncFailedReasonCode: 0,
        lastSynced: "",
        isExportEnabled: false,
        isContactExportEnabled: false,
        isLeadsExportEnabled: false,
        isCompanyAsLeadExportEnabled: false,
        isLeadsFilterEnabled: false,
        isCrmListEnabled: false,
        isCrmLabelsEnabled: false,
        isCrmFeedEnabled: false,
        crmFeedLevel: CrmFeedLevel.User,
        crmListObjectsType: CRMListObjectsType.AccountsAndLeads,
    },
    crmStateInfo: {
        accountExportFieldsCount: 0,
        contactExportFieldsCount: 0,
        leadExportFieldsCount: 0,
    },
    crmExportLimit: null,
    companyAsLeadExportClaimEnabled: false,
    predefinedOnCreateUpdateClaimEnabled: false,
    isCurrentUserVerifiedByCrm: false,
    toggleLoading: {
        isContactsLoading: false,
        isLeadsLoading: false,
        isAccountsLoading: false,
    },
    excludeStatusFields: [],
    contactFields: [],
    leadFields: [],
    isLoadingMappingFields: false,
    mappingFields: [],
    mappingFieldsError: null,
    isLoadingExcludeStatusFields: false,
    isExclusionOn: null,
    isExclusionLoading: false,
    exclusionError: "",
    error: "",
    isLoading: false,
    excludeAccountFields: [],
    isLoadingExcludeAccountFields: false,
    isUpdatingFilterFields: false,
    isUpdatingStatusField: false,
};

export const salesforceIntegrationReducer = createReducer<
    SalesforceIntegrationState,
    ActionType<typeof managementActions.salesforce>
>(SALESFORCE_INTEGRATION_STATE)
    .handleAction(managementActions.salesforce.getSalesforceStatusAsync.success, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: payload.crmConnectionStatus,
        crmStateInfo: payload.crmStateInfo,
        crmExportLimit: payload.crmExportLimit ?? null,
        isCurrentUserVerifiedByCrm: payload.isCurrentUserVerifiedByCrm,
        companyAsLeadExportClaimEnabled: payload.companyAsLeadExportClaimEnabled,
        predefinedOnCreateUpdateClaimEnabled: payload.predefinedOnCreateUpdateClaimEnabled,
        isLoading: false,
    }))
    .handleAction(managementActions.salesforce.getSalesforceStatusAsync.failure, (state, { payload }) => ({
        ...state,
        error: payload,
        isLoading: false,
    }))
    .handleAction(managementActions.salesforce.getSalesforceStatusAsync.request, (state, { payload }) => ({
        ...state,
        isLoading: payload,
    }))
    .handleAction(managementActions.salesforce.getExcludeStatusFieldsAsync.request, (state) => ({
        ...state,
        isLoadingExcludeStatusFields: true,
    }))
    .handleAction(managementActions.salesforce.getExcludeStatusFieldsAsync.success, (state, { payload }) => ({
        ...state,
        excludeStatusFields: payload,
        isLoadingExcludeStatusFields: false,
    }))
    .handleAction(managementActions.salesforce.getSfContactsFieldsAsync.success, (state, { payload }) => ({
        ...state,
        contactFields: payload,
    }))
    .handleAction(managementActions.salesforce.getSfLeadsFieldsAsync.success, (state, { payload }) => ({
        ...state,
        leadFields: payload,
    }))
    .handleAction(managementActions.salesforce.getExcludeStatusFieldsAsync.failure, (state, { payload }) => ({
        ...state,
        isLoadingExcludeStatusFields: false,
        error: payload,
    }))
    .handleAction(managementActions.salesforce.getExcludeAccountFieldsAsync.request, (state) => ({
        ...state,
        isLoadingExcludeAccountFields: true,
    }))
    .handleAction(managementActions.salesforce.getExcludeAccountFieldsAsync.success, (state, { payload }) => ({
        ...state,
        excludeAccountFields: payload,
        isLoadingExcludeAccountFields: false,
    }))
    .handleAction(managementActions.salesforce.getExcludeAccountFieldsAsync.failure, (state, { payload }) => ({
        ...state,
        isLoadingExcludeAccountFields: false,
        error: payload,
    }))
    .handleAction(managementActions.salesforce.getMappingFieldsAsync.request, (state) => ({
        ...state,
        isLoadingMappingFields: true,
        mappingFieldsError: null,
    }))
    .handleAction(managementActions.salesforce.getMappingFieldsAsync.success, (state, { payload }) => ({
        ...state,
        mappingFields: payload,
        isLoadingMappingFields: false,
    }))
    .handleAction(managementActions.salesforce.getMappingFieldsAsync.failure, (state, { payload }) => ({
        ...state,
        mappingFieldsError: payload,
        isLoadingMappingFields: false,
    }))
    .handleAction(managementActions.salesforce.setSyncStatus, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            syncStatus: payload,
        },
    }))
    .handleAction(managementActions.salesforce.disconnectSalesforceAsync.request, (state) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            syncStatus: INTEGRATION_STATUS.InProgress,
        },
        isLoading: true,
    }))
    .handleAction(managementActions.salesforce.setIsSfAccountsExportEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            isExportEnabled: payload,
        },
    }))
    .handleAction(managementActions.salesforce.setIsSfContactsExportEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            isContactsExportEnabled: payload,
        },
    }))
    .handleAction(managementActions.salesforce.setIsSfLeadsExportEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            isLeadsExportEnabled: payload,
        },
    }))
    .handleAction(managementActions.salesforce.disconnectSalesforceAsync.success, (state) => ({
        ...state,
        crmConnectionStatus: { ...SALESFORCE_INTEGRATION_STATE.crmConnectionStatus },
        isLoading: false,
    }))
    .handleAction(managementActions.salesforce.disconnectSalesforceAsync.failure, (state) => ({
        ...state,
        crmConnectionStatus: { ...SALESFORCE_INTEGRATION_STATE.crmConnectionStatus },
        isLoading: false,
    }))
    .handleAction(managementActions.salesforce.putIntegrationLeadFilterStatusAsync.request, (state) => ({
        ...state,
        isExclusionLoading: true,
    }))
    .handleAction(managementActions.salesforce.putIntegrationLeadFilterStatusAsync.success, (state, { payload }) => ({
        ...state,
        isExclusionOn: payload,
        isExclusionLoading: false,
    }))
    .handleAction(managementActions.salesforce.putIntegrationLeadFilterStatusAsync.failure, (state, { payload }) => ({
        ...state,
        exclusionError: payload,
        isExclusionLoading: false,
    }))
    .handleAction(managementActions.salesforce.getIntegrationLeadFilterStatusAsync.request, (state) => ({
        ...state,
        isExclusionLoading: true,
    }))
    .handleAction(managementActions.salesforce.getIntegrationLeadFilterStatusAsync.success, (state, { payload }) => ({
        ...state,
        isExclusionOn: payload,
        isExclusionLoading: false,
    }))
    .handleAction(managementActions.salesforce.getIntegrationLeadFilterStatusAsync.failure, (state, { payload }) => ({
        ...state,
        exclusionError: payload,
        isExclusionLoading: false,
    }))
    .handleAction(managementActions.salesforce.putFilterFieldsAsync.request, (state) => ({
        ...state,
        isUpdatingFilterFields: true,
    }))
    .handleAction(managementActions.salesforce.putFilterFieldsAsync.success, (state) => ({
        ...state,
        isUpdatingFilterFields: false,
    }))
    .handleAction(managementActions.salesforce.putFilterFieldsAsync.failure, (state) => ({
        ...state,
        isUpdatingFilterFields: false,
    }))
    .handleAction(managementActions.salesforce.putStatusFieldAsync.request, (state) => ({
        ...state,
        isUpdatingStatusField: true,
    }))
    .handleAction(managementActions.salesforce.putStatusFieldAsync.success, (state) => ({
        ...state,
        isUpdatingStatusField: false,
    }))
    .handleAction(managementActions.salesforce.putStatusFieldAsync.failure, (state) => ({
        ...state,
        isUpdatingStatusField: false,
    }))
    .handleAction(managementActions.salesforce.putContactsToggleAsync.success, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: payload.crmConnectionStatus,
        toggleLoading: { ...state.toggleLoading, isContactsLoading: false },
    }))
    .handleAction(managementActions.salesforce.putContactsToggleAsync.request, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isContactsLoading: true },
    }))
    .handleAction(managementActions.salesforce.putContactsToggleAsync.failure, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isContactsLoading: false },
    }))
    .handleAction(managementActions.salesforce.putAccountsToggleAsync.success, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: payload.crmConnectionStatus,
        toggleLoading: { ...state.toggleLoading, isAccountsLoading: false },
    }))
    .handleAction(managementActions.salesforce.putAccountsToggleAsync.request, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isContactsLoading: true },
    }))
    .handleAction(managementActions.salesforce.putAccountsToggleAsync.failure, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isAccountsLoading: false },
    }))
    .handleAction(managementActions.salesforce.putLeadsToggleAsync.success, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: payload.crmConnectionStatus,
        toggleLoading: { ...state.toggleLoading, isLeadsLoading: false },
    }))
    .handleAction(managementActions.salesforce.putLeadsToggleAsync.request, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isLeadsLoading: true },
    }))
    .handleAction(managementActions.salesforce.putLeadsToggleAsync.failure, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isLeadsLoading: false },
    }))
    .handleAction(managementActions.salesforce.putCompanyAsLeadsToggleAsync.success, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: payload.crmConnectionStatus,
        toggleLoading: { ...state.toggleLoading, isLeadsLoading: false },
    }))
    .handleAction(managementActions.salesforce.putCompanyAsLeadsToggleAsync.request, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isLeadsLoading: true },
    }))
    .handleAction(managementActions.salesforce.putCompanyAsLeadsToggleAsync.failure, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isLeadsLoading: false },
    }))
    .handleAction(managementActions.salesforce.saveSFExportSettingsAsync.success, (state, { payload }) => {
        const exportFieldsCountKey = (() => {
            switch (payload.typeName) {
                case "accounts":
                    return "accountExportFieldsCount";
                case "contacts":
                    return "contactExportFieldsCount";
                default:
                    return "leadExportFieldsCount";
            }
        })();

        return {
            ...state,
            crmStateInfo: {
                ...state.crmStateInfo,
                [exportFieldsCountKey]: payload.settings.length,
            },
        };
    })
    .handleAction(managementActions.salesforce.setIsSfCrmLabelEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: { ...state.crmConnectionStatus, isCrmLabelsEnabled: payload },
    }))
    .handleAction(managementActions.salesforce.setIsSfFeedEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: { ...state.crmConnectionStatus, isCrmFeedEnabled: payload },
    }))
    .handleAction(managementActions.salesforce.setSfCrmFeedLevel, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: { ...state.crmConnectionStatus, crmFeedLevel: payload },
    }))
    .handleAction(managementActions.salesforce.setIsSfCrmListEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: { ...state.crmConnectionStatus, isCrmListEnabled: payload },
    }))
    .handleAction(managementActions.salesforce.setSfCrmListObjectsType, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: { ...state.crmConnectionStatus, crmListObjectsType: payload },
    }));
